import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  MessageCircle, Bot, UserPlus, Users, Bell, ChevronRight,
  CheckCircle2, Lock, Circle,
} from 'lucide-react';

const STEP_ICONS = {
  whatsapp_receive: MessageCircle,
  ai_reply: Bot,
  crm_convert: UserPlus,
  assign_seller: Users,
  notify_seller: Bell,
};

function PlanCalculator({ planFeatures }) {
  if (!planFeatures?.features?.length) return null;
  const { plan_name, features, included_count, total_count } = planFeatures;
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4" data-testid="plan-calculator">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">Tu plan</p>
          <p className="text-xs text-[#64748B]">{plan_name || 'Plan activo'} · {included_count}/{total_count} funciones incluidas</p>
        </div>
        <Button variant="outline" size="sm" className="text-xs" onClick={() => window.location.href = '/tenant/plan'}>
          Ver plan
        </Button>
      </div>
      <div className="space-y-1.5">
        {features.map((f) => (
          <div key={f.key} className="flex items-start gap-2 text-xs">
            {f.included
              ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
              : <Lock className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />}
            <div>
              <span className={f.included ? 'text-[#334155]' : 'text-[#94A3B8]'}>{f.label}</span>
              {!f.included && <p className="text-[10px] text-amber-600">{f.upgrade_hint}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AttendanceFlowPanel({ flow, flowSteps, planFeatures, onSaved }) {
  const [steps, setSteps] = useState(flow?.steps || []);
  const [settings, setSettings] = useState(flow?.settings || {});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (flow?.steps) setSteps(flow.steps);
    if (flow?.settings) setSettings(flow.settings);
  }, [flow]);

  const toggleStep = (key) => {
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, enabled: !s.enabled } : s)));
  };

  const saveFlow = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/lead-attendance/flow', { steps, settings });
      toast.success('Flujo guardado');
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const displaySteps = flowSteps?.length ? flowSteps : steps;

  return (
    <div className="space-y-4" data-testid="attendance-flow-panel">
      <PlanCalculator planFeatures={planFeatures} />

      <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between text-left"
        >
          <div>
            <p className="text-sm font-semibold text-[#0F172A]">Flujo de atención</p>
            <p className="text-xs text-[#64748B]">Qué pasa cuando un cliente te escribe por WhatsApp</p>
          </div>
          <ChevronRight className={`w-4 h-4 text-[#94A3B8] transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>

        {expanded && (
          <div className="mt-4 space-y-2">
            {displaySteps.map((step, i) => {
              const Icon = STEP_ICONS[step.key] || Circle;
              const blocked = step.blocked_by_plan;
              const runtimeOk = step.runtime_ok;
              return (
                <div key={step.key} className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                  <div className="w-8 h-8 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#475569]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{step.label}</p>
                    {blocked && <p className="text-[10px] text-amber-600">No incluido en tu plan</p>}
                    {!blocked && runtimeOk === true && <p className="text-[10px] text-emerald-600">Activo</p>}
                    {!blocked && runtimeOk === false && step.enabled !== false && (
                      <p className="text-[10px] text-[#94A3B8]">Pendiente de configurar</p>
                    )}
                  </div>
                  {!blocked && (
                    <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={steps.find((s) => s.key === step.key)?.enabled ?? step.enabled}
                        onChange={() => toggleStep(step.key)}
                        className="rounded border-[#CBD5E1]"
                      />
                      <span className="text-[10px] text-[#64748B]">On</span>
                    </label>
                  )}
                  {i < displaySteps.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-[#CBD5E1] absolute hidden" />
                  )}
                </div>
              );
            })}

            <label className="flex items-center gap-2 mt-3 text-sm text-[#334155] cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notify_seller_on_assign !== false}
                onChange={(e) => setSettings((s) => ({ ...s, notify_seller_on_assign: e.target.checked }))}
                className="rounded border-[#CBD5E1]"
              />
              Avisar al vendedor por WhatsApp al asignar lead
            </label>

            <Button onClick={saveFlow} disabled={saving} size="sm" className="w-full bg-[#0F172A] text-white mt-2">
              {saving ? 'Guardando...' : 'Guardar flujo'}
            </Button>
          </div>
        )}

        {!expanded && (
          <div className="flex items-center gap-1 mt-3 overflow-x-auto pb-1">
            {displaySteps.filter((s) => s.enabled !== false).map((step, i, arr) => {
              const Icon = STEP_ICONS[step.key] || Circle;
              return (
                <div key={step.key} className="flex items-center gap-1 flex-shrink-0">
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border ${
                    step.runtime_ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-[#F1F5F9] border-[#E2E8F0] text-[#64748B]'
                  }`}>
                    <Icon className="w-3 h-3" />
                    <span>{step.label.split(' ').slice(0, 2).join(' ')}</span>
                  </div>
                  {i < arr.length - 1 && <ChevronRight className="w-3 h-3 text-[#CBD5E1]" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}